"use client";

import React from 'react';
import { Sparkles } from 'lucide-react';
import { useTranslations } from 'next-intl';

const ObjectionHandlingSection: React.FC = () => {
  const t = useTranslations();

  const objections = [
    {
      question: t('obj_q1'),
      answer: t('obj_a1'),
      accent: 'from-blue-500/20 to-blue-600/5',
      border: 'border-blue-500/20',
      dot: 'bg-blue-500',
    },
    {
      question: t('obj_q2'),
      answer: t('obj_a2'),
      accent: 'from-purple-500/20 to-purple-600/5',
      border: 'border-purple-500/20',
      dot: 'bg-purple-500',
    },
    {
      question: t('obj_q3'),
      answer: t('obj_a3'),
      accent: 'from-cyan-500/20 to-cyan-600/5',
      border: 'border-cyan-500/20',
      dot: 'bg-cyan-500',
    },
    {
      question: t('obj_q4'),
      answer: t('obj_a4'),
      accent: 'from-green-500/20 to-green-600/5',
      border: 'border-green-500/20',
      dot: 'bg-green-500',
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-slate-900 relative overflow-hidden" id="objections">
      {/* Background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-primary/8 rounded-full blur-[100px] pointer-events-none" />

      <div className="container-std relative z-10">
        {/* Header */}
        <div className="text-center mb-16 max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary mb-6">
            <span className="text-xs font-bold uppercase tracking-wider">
              {t('objections_badge')}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-foreground mb-4">
            {t('objections_title')}
          </h2>
          <p className="text-xl text-primary font-bold">
            {t('objections_subtitle')}
          </p>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto">
          {objections.map((obj, idx) => (
            <div
              key={idx}
              className={`relative rounded-2xl border ${obj.border} bg-gradient-to-br ${obj.accent} p-6 overflow-hidden`}
            >
              {/* Question */}
              <div className="flex items-start gap-3 mb-4">
                <div className={`mt-1.5 w-2 h-2 rounded-full ${obj.dot} shrink-0`} />
                <p className="text-foreground font-bold text-base leading-snug">
                  «{obj.question}»
                </p>
              </div>

              {/* Divider */}
              <div className="h-px bg-white/10 mb-4" />

              {/* Answer */}
              <p className="text-muted-foreground text-sm leading-relaxed">
                {obj.answer}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom hint */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-primary/10 border border-primary/20">
            <Sparkles size={16} className="text-primary" />
            <span className="text-foreground text-sm font-medium">
              {t('objections_cta')}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ObjectionHandlingSection;
